/* proposal_portal.js — live pricing for the election proposal portal */
document.addEventListener('turbolinks:load', () => {
  const portal = document.getElementById('proposalPortal');
  if (!portal) return;

  // ── Price tiers (mirror PaymentLineItem::NOUVEAU_TIERS) ───────────────
  const PRICE_TIERS = {
    nomination_form: [
      [0, 99, 3.25], [100, 149, 3.00],
      [150, 299, 2.75], [300, 1000, 2.25],
      [1001, Infinity, 2.00]
    ],
    pre_ballot: [
      [0, 99, 3.25], [100, 149, 3.00],
      [150, 299, 2.75], [300, 1000, 2.25],
      [1001, Infinity, 1.75]
    ],
    ballot: [
      [0, 99, 4.75], [100, 149, 4.50],
      [150, 299, 4.25], [300, 1000, 3.75],
      [1001, Infinity, 3.50]
    ],
    email: [
      [0, 99, 0.38], [100, 149, 0.35],
      [150, 299, 0.28], [300, Infinity, 0.25]
    ],
    inspector: [
      [0, 0, 0], [1, 20, 350], [21, 199, 450], [200, Infinity, 650]
    ]
  };

  // Physical-mailing tiers (nomination_form / pre_ballot / ballot) get a per-unit
  // increase for bids on or after the increase date. The amount ($0.05 or 0) is
  // supplied by the server per bid so the live estimate matches the billed,
  // date-gated line items. Email + inspector are never increased.
  const MAILING_RATE_INCREASE = parseFloat(portal.dataset.mailingRateIncrease) || 0;
  const INCREASED_TIERS = ['nomination_form', 'pre_ballot', 'ballot'];

  const price = (tier, units) => {
    const row = PRICE_TIERS[tier].find(([lo, hi]) => units >= lo && units <= hi);
    if (!row) return 0;
    return INCREASED_TIERS.includes(tier)
      ? Math.round((row[2] + MAILING_RATE_INCREASE) * 100) / 100
      : row[2];
  };

  // In-person fee — mirrors PaymentLineItem#make_in_person, uses total units.
  // All units (paper + electronic) count. Scales above 200: +$150 per 100 units.
  const inPersonFee = (units) => {
    if (units <= 20)  return 150;
    if (units <= 200) return 300;
    return 600 + Math.floor((units - 201) / 100) * 150;
  };

  const fmt = n => '$' + n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content || '';

  // ── DOM references ────────────────────────────────────────────────────
  const totalUnits      = parseInt(portal.dataset.units, 10) || 0;
  let   mailedUnits     = parseInt(portal.dataset.mailedUnits, 10) || 0;
  let   electronicUnits = totalUnits - mailedUnits;

  const timelineUrl            = portal.dataset.timelineUrl            || (window.location.pathname + '/timeline');
  const updateMeetingDateUrl   = portal.dataset.updateMeetingDateUrl   || (window.location.pathname + '/update_meeting_date');
  const configureUrl           = portal.dataset.configureUrl           || (window.location.pathname + '/configure');
  const updateUnitsUrl         = portal.dataset.updateUnitsUrl         || (window.location.pathname + '/update_units');
  const updatePagesUrl         = portal.dataset.updatePagesUrl         || (window.location.pathname + '/update_pages');
  const updateElectionTypeUrl  = portal.dataset.updateElectionTypeUrl  || (window.location.pathname + '/update_election_type');
  const updateElectionDepthUrl = portal.dataset.updateElectionDepthUrl || (window.location.pathname + '/update_election_depth');

  const nomCheck       = document.getElementById('service_nomination_forms');
  const preBallotCheck = document.getElementById('service_pre_ballots');
  const ballotsCheck   = document.getElementById('service_ballots');
  const ecfCheck       = document.getElementById('service_electronic_consent_form');
  const rulesCheck     = document.getElementById('service_electronic_voting_rules_confirmed');
  const inPersonCheck  = document.getElementById('service_in_person');
  const breakdownEl    = document.getElementById('priceBreakdown');
  const totalEl        = document.getElementById('priceTotal');
  const timelineEl          = document.getElementById('proposalTimeline');
  const timelineSummary     = document.getElementById('timelineSummary');
  const timelineHeading     = document.getElementById('timelineHeading');
  const timelineStatusBadge = document.getElementById('timelineStatusBadge');
  const meetingDateInput    = document.getElementById('meetingDateInput');
  const dateChangeIndicator = document.getElementById('dateChangeIndicator');
  const rulesWarning   = document.getElementById('rulesWarning');
  const ecfNote        = document.getElementById('ecfPaymentNote');
  const standardNote   = document.getElementById('standardPaymentNote');
  const unitAdjuster   = document.getElementById('unitAdjuster');
  const paperInput     = document.getElementById('paperUnitsInput');
  const electronicInput = document.getElementById('electronicUnitsInput');
  const governingPagesInput = document.getElementById('governingPagesInput');

  // Governing-document page count (Change in Governing Documents). The first
  // GOVERNING_DOC_INCLUDED_PAGES are free; extra pages are billed per printed unit.
  const GOVERNING_DOC_INCLUDED_PAGES = 4;
  // Date-gated per bid ($0.20 before / $0.22 on or after the increase date),
  // supplied by the server so the live estimate matches the billed line items.
  const GOVERNING_DOC_PAGE_RATE      = parseFloat(portal.dataset.governingDocPageRate) || 0.20;
  let   governingPages = parseInt(portal.dataset.governingPages, 10) || GOVERNING_DOC_INCLUDED_PAGES;

  // ── Helpers ───────────────────────────────────────────────────────────
  const rulesConfirmed   = () => rulesCheck?.checked ?? true;
  const anyServiceChecked = () =>
    (nomCheck?.checked) || (preBallotCheck?.checked) || (ballotsCheck?.checked);

  const lineItem = (label, cost) =>
    `<div class="flex justify-between items-baseline gap-4">` +
      `<span class="text-gray-500 text-sm">${label}</span>` +
      `<span class="flex-shrink-0 text-gray-800 tabular-nums text-sm">${fmt(cost)}</span>` +
    `</div>`;

  // ── Electronic description lines — hidden when rules not confirmed or no electronic units ──
  const syncElectronicDescs = () => {
    const show = rulesConfirmed() && electronicUnits > 0;
    document.querySelectorAll('.service-electronic-desc').forEach(el => {
      el.classList.toggle('hidden', !show);
    });
  };

  // ── ECF payment note — keyed only to ECF checkbox ─────────────────────
  // Standard note shows when ECF note is hidden and vice-versa.
  const syncEcfNote = () => {
    const ecfActive = ecfCheck?.checked && !ecfCheck?.disabled;
    if (ecfNote)      ecfNote.classList.toggle('hidden', !ecfActive);
    if (standardNote) standardNote.classList.toggle('hidden',  ecfActive);
  };

  // ── Unit adjuster — keyed to rules confirmed ──────────────────────────
  const syncUnitAdjuster = () => {
    if (!unitAdjuster) return;
    unitAdjuster.classList.toggle('hidden', !rulesConfirmed());
  };

  // ── Rules-confirmation state ──────────────────────────────────────────
  // confirmed:    whether rules are now confirmed
  // autoCheckEcf: true only when called from a direct user click on the rules
  //               checkbox — NEVER when called from applyServerResponse.
  //               This prevents every server round-trip from re-checking ECF
  //               and blocking the user from deselecting it.
  const applyRulesState = (confirmed, autoCheckEcf = false) => {
    const ecfRow = document.getElementById('ecfRow');
    if (confirmed) {
      if (ecfRow)    ecfRow.classList.remove('hidden');
      if (ecfCheck) {
        ecfCheck.disabled = false;
        if (autoCheckEcf && anyServiceChecked()) ecfCheck.checked = true;
      }
      if (rulesWarning) rulesWarning.classList.add('hidden');
    } else {
      if (ecfRow)    ecfRow.classList.add('hidden');
      if (ecfCheck) {
        ecfCheck.checked  = false;
        ecfCheck.disabled = true;
      }
      if (rulesWarning) rulesWarning.classList.remove('hidden');
    }
    syncUnitAdjuster();
    syncEcfNote();
    syncElectronicDescs();
  };

  // Only auto-uncheck ECF when every service is deselected.
  // Never force ECF back on — once the user unchecks it, it stays unchecked
  // until rules are toggled again (which triggers the one-time auto-check in applyRulesState).
  const syncEcfDefault = () => {
    if (!ecfCheck || ecfCheck.disabled) return;
    if (!anyServiceChecked() && ecfCheck.checked) {
      ecfCheck.checked = false;
      syncEcfNote();
    }
  };

  // ── Pricing recalculation ──────────────────────────────────────────────
  // Electronic unit line items are only shown when rules are confirmed.
  const recalc = () => {
    const lines      = [];
    let   total      = 0;
    const showElec   = rulesConfirmed();
    // When rules not confirmed, every unit receives a paper mailing
    const paperUnits = showElec ? mailedUnits : totalUnits;

    if (isAcclamation) {
      const acclamationFee = totalUnits < 21 ? 50 : 100;
      lines.push(lineItem('Acclamation Service', acclamationFee));
      total += acclamationFee;
    } else {
      const inspectorFee = price('inspector', paperUnits);
      lines.push(lineItem('Inspector of Elections', inspectorFee));
      total += inspectorFee;
    }

    if (ecfCheck?.checked && !ecfCheck.disabled) {
      if (paperUnits > 0) {
        const cost = price('nomination_form', paperUnits) * paperUnits;
        lines.push(lineItem(`Electronic Consent Notice — paper (${paperUnits} units)`, cost));
        total += cost;
      }
      if (electronicUnits > 0 && showElec) {
        const cost = price('email', electronicUnits) * electronicUnits;
        lines.push(lineItem(`Electronic Consent Notice — electronic (${electronicUnits} units)`, cost));
        total += cost;
      }
    }

    // Nomination Form Mailing and the Reminder are mandatory for acclamation
    // (the checkboxes are locked on), so price them whenever isAcclamation —
    // never gate on !disabled, or the locked-on EBA box would be skipped and
    // the nomination form would silently drop out of the estimate. For other
    // election types they follow the checkbox (a disabled box there is RCL,
    // which is locked off, so .checked already excludes it).
    // Nomination forms do not apply to a Change in Governing Documents (CGD) or
    // an Other (OTH) election.
    const nominationFormOn = !['CGD', 'OTH'].includes(currentElectionValue) && (isAcclamation || !!nomCheck?.checked);
    const preBallotOn      = isAcclamation || !!preBallotCheck?.checked;

    if (nominationFormOn && paperUnits > 0) {
      const cost = price('nomination_form', paperUnits) * paperUnits;
      lines.push(lineItem(`Nomination Form — paper (${paperUnits} units)`, cost));
      total += cost;
    }
    if (nominationFormOn && electronicUnits > 0 && showElec) {
      const cost = price('email', electronicUnits) * electronicUnits;
      lines.push(lineItem(`Nomination Form — electronic (${electronicUnits} units)`, cost));
      total += cost;
    }

    if (preBallotOn && paperUnits > 0) {
      const label = isAcclamation ? 'Nomination Form Reminder' : 'Pre-Ballot Notification';
      const cost  = price('pre_ballot', paperUnits) * paperUnits;
      lines.push(lineItem(`${label} — paper (${paperUnits} units)`, cost));
      total += cost;
    }
    if (preBallotOn && electronicUnits > 0 && showElec) {
      const elecCost = price('email', electronicUnits) * electronicUnits;
      const label = isAcclamation ? 'Nomination Form Reminder' : 'Pre-Ballot Notification';
      lines.push(lineItem(`${label} — electronic (${electronicUnits} units)`, elecCost));
      total += elecCost;
    }

    if (ballotsCheck?.checked && paperUnits > 0) {
      const cost = price('ballot', paperUnits) * paperUnits;
      lines.push(lineItem(`Ballot Mailing — paper (${paperUnits} units)`, cost));
      total += cost;
    }
    if (ballotsCheck?.checked && electronicUnits > 0 && showElec) {
      const cost = price('email', electronicUnits) * electronicUnits;
      lines.push(lineItem(`Ballot Mailing — electronic (${electronicUnits} units)`, cost));
      total += cost;
    }

    // Governing-document printing — Change in Governing Documents only. The
    // first GOVERNING_DOC_INCLUDED_PAGES pages are free; each extra page is
    // billed per printed unit (paperUnits already accounts for the electronic
    // split, matching PaymentLineItem.make_governing_documents on the server).
    if (currentElectionValue === 'CGD') {
      const extraPages = Math.max(governingPages - GOVERNING_DOC_INCLUDED_PAGES, 0);
      if (extraPages > 0 && paperUnits > 0) {
        const cost = extraPages * paperUnits * GOVERNING_DOC_PAGE_RATE;
        lines.push(lineItem('Additional Printing and Postage Fees', cost));
        total += cost;
      }
    }

    if (inPersonCheck?.checked && !inPersonCheck.disabled) {
      const fee = inPersonFee(totalUnits);
      lines.push(lineItem('Inspector of Election Services: In Person', fee));
      total += fee;
    }

    breakdownEl.innerHTML = lines.join('');
    totalEl.textContent = fmt(total);
  };

  // ── Server sync ───────────────────────────────────────────────────────
  const applyServerResponse = (data) => {
    if (typeof data.estimated_mailed_units === 'number') {
      mailedUnits     = data.estimated_mailed_units;
      electronicUnits = totalUnits - mailedUnits;
      if (paperInput)      paperInput.value      = mailedUnits;
      if (electronicInput) electronicInput.value = electronicUnits;
    }
    if (typeof data.rules_confirmed === 'boolean') {
      applyRulesState(data.rules_confirmed, false); // never auto-check ECF from server response
    }
    if (typeof data.pages === 'number') {
      governingPages = data.pages;
      if (governingPagesInput) governingPagesInput.value = data.pages;
    }
    if (data.line_items) {
      breakdownEl.innerHTML = data.line_items
        .map(item => lineItem(item.title, item.value))
        .join('');
      totalEl.textContent = fmt(data.total);
    }
    syncEcfNote();
    syncUnitAdjuster();
    syncElectronicDescs();
    fetchTimeline();
    checkDocumentFreshness();
  };

  const patchField = (field, value) =>
    fetch(configureUrl, {
      method:  'PATCH',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken(), 'Accept': 'application/json' },
      body:    JSON.stringify({ field, value })
    }).then(r => r.ok ? r.json() : Promise.reject(r.status)).then(applyServerResponse).catch(() => {});

  const patchUnits = (paper) =>
    fetch(updateUnitsUrl, {
      method:  'PATCH',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken(), 'Accept': 'application/json' },
      body:    JSON.stringify({ paper_units: paper })
    }).then(r => r.ok ? r.json() : Promise.reject(r.status)).then(applyServerResponse).catch(() => {});

  const patchPages = (pages) =>
    fetch(updatePagesUrl, {
      method:  'PATCH',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken(), 'Accept': 'application/json' },
      body:    JSON.stringify({ pages })
    }).then(r => r.ok ? r.json() : Promise.reject(r.status)).then(applyServerResponse).catch(() => {});

  // ── Service lock/unlock helpers ───────────────────────────────────────
  const lockService = (cb, checked) => {
    if (!cb) return;
    cb.checked  = checked;
    cb.disabled = true;
    cb.closest('label')?.classList.add('opacity-60', 'cursor-not-allowed');
    cb.closest('label')?.classList.remove('cursor-pointer');
  };

  const unlockService = (cb, checked) => {
    if (!cb) return;
    cb.checked  = checked;
    cb.disabled = false;
    cb.closest('label')?.classList.remove('opacity-60', 'cursor-not-allowed');
    cb.closest('label')?.classList.add('cursor-pointer');
  };

  // Tracks whether the current election type is acclamation (EBA).
  // Initialised from the server-rendered data attribute; updated on each type switch.
  let isAcclamation = portal.dataset.electionValue === 'EBA';

  // Snapshot of ANE service state captured just before switching to EBA,
  // used to restore when switching back.
  let aneSnapshot = null;

  // ── Document freshness tracking ───────────────────────────────────────────
  // currentElectionValue stays in sync with every server-confirmed type switch.
  let currentElectionValue = portal.dataset.electionValue;

  // The last snapshot that was included in a "Send Documents" email.
  // null = documents have never been sent.  Loaded from the server on page load.
  let sentSnapshot = null;

  // Banner dismissed for this session — reset on next page load.
  let bannerDismissed = false;

  const banner      = document.getElementById('proposalDocumentsBanner');
  const bannerIcon  = document.getElementById('proposalDocumentsBannerIcon');
  const bannerTitle = document.getElementById('proposalDocumentsBannerTitle');
  const bannerBody  = document.getElementById('proposalDocumentsBannerBody');
  const bannerClose = document.getElementById('proposalDocumentsBannerClose');

  // Returns a plain object representing every user-configurable proposal value.
  function currentSnapshot() {
    const ebaQualRadio = document.querySelector('.eba-qualification-radio:checked');
    return {
      election_value:  currentElectionValue,
      eba_qualified:   ebaQualRadio ? ebaQualRadio.value === 'true' : null,
      request_forms:   !!(nomCheck?.checked && !nomCheck.disabled),
      pre_ballots:     !!(preBallotCheck?.checked && !preBallotCheck.disabled),
      ballots:         !!(ballotsCheck?.checked && !ballotsCheck.disabled),
      ecf:             !!(ecfCheck?.checked && !ecfCheck.disabled),
      rules_confirmed: !!(rulesCheck?.checked),
      in_person:       !!(inPersonCheck?.checked && !inPersonCheck.disabled),
      mailed_units:    mailedUnits,
      meeting_date:    meetingDateInput?.value || null
    };
  }

  function snapshotsMatch(a, b) {
    if (!a || !b) return false;
    return JSON.stringify(a) === JSON.stringify(b);
  }

  const BANNER_INFO = {
    borderColor: 'border-blue-300',
    bg:          'bg-blue-50',
    textColor:   'text-blue-800',
    iconColor:   'text-blue-500'
  };
  const BANNER_WARN = {
    borderColor: 'border-amber-400',
    bg:          'bg-amber-50',
    textColor:   'text-amber-900',
    iconColor:   'text-amber-500'
  };

  function applyBannerTheme(theme) {
    if (!banner) return;
    banner.className = banner.className
      .replace(/border-\S+|bg-\S+|text-\S+/g, '')
      .trim();
    banner.classList.add(
      'mb-8', 'border-l-4', 'px-4', 'py-4', 'flex', 'items-start', 'gap-3',
      theme.borderColor, theme.bg, theme.textColor
    );
    if (bannerIcon) bannerIcon.className = `flex-shrink-0 mt-0.5 ${theme.iconColor}`;
  }

  function checkDocumentFreshness() {
    if (!banner || bannerDismissed) return;

    if (sentSnapshot === null) {
      applyBannerTheme(BANNER_INFO);
      bannerTitle.textContent = `Welcome to the proposal portal for ${portal.dataset.hoaName || 'your association'}.`;
      bannerBody.textContent  = 'Review your services and options below. When you\'re ready, use the "Send Documents" button to email the Service Agreement, Payment Schedule, and Election Timeline to your contact.';
      banner.classList.remove('hidden');
    } else if (!snapshotsMatch(currentSnapshot(), sentSnapshot)) {
      applyBannerTheme(BANNER_WARN);
      bannerTitle.textContent = 'Your proposal has changed since the documents were last sent.';
      bannerBody.textContent  = 'Review your updates below and send a new copy to your contact so they have the latest documents.';
      banner.classList.remove('hidden');
      // Reset the send button so it's ready to use again.
      if (sendLabel) sendLabel.textContent = 'Send Documents';
      if (sendBtn)   sendBtn.disabled      = false;
      if (sendStatus) { sendStatus.textContent = ''; sendStatus.className = 'mt-3 text-sm hidden'; }
    } else {
      banner.classList.add('hidden');
    }
  }

  if (bannerClose) {
    bannerClose.addEventListener('click', () => {
      bannerDismissed = true;
      banner.classList.add('hidden');
    });
  }

  // Load the last-sent snapshot from the server so the banner is accurate on every page load.
  const sentSnapshotUrl = portal.dataset.sentSnapshotUrl;
  if (sentSnapshotUrl) {
    fetch(sentSnapshotUrl, { headers: { Accept: 'application/json' } })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(data => {
        sentSnapshot = data.snapshot || null;
        checkDocumentFreshness();
      })
      .catch(() => { checkDocumentFreshness(); });
  }

  const applyElectionTypeResponse = (data) => {
    if (data.line_items) {
      breakdownEl.innerHTML = data.line_items
        .map(item => lineItem(item.title, item.value))
        .join('');
      totalEl.textContent = fmt(data.total);
    }

    // Header title reflects the server-confirmed election type
    const electionTypeLabel = document.getElementById('electionTypeLabel');
    if (electionTypeLabel && data.type_of_election) {
      electionTypeLabel.textContent = data.type_of_election;
    }

    // EBA caveat visibility
    const caveat = document.getElementById('ebaCaveat');
    if (caveat) caveat.classList.toggle('hidden', data.election_value !== 'EBA');

    // Pre-ballot label and description switch on acclamation
    const preBallotTitle      = document.getElementById('preBallotTitle');
    const preBallotDescText   = document.getElementById('preBallotDescText');
    const preBallotLearnMore  = document.getElementById('preBallotLearnMore');
    if (data.is_acclamation) {
      if (preBallotTitle)    preBallotTitle.textContent    = 'Nomination Form Reminder';
      if (preBallotDescText) preBallotDescText.textContent = 'A single-page stamped mailing to each unit serving as a second reminder that the nomination period is approaching its close';
      if (preBallotLearnMore) preBallotLearnMore.href      = portal.dataset.aboutNominationReminderUrl;
    } else {
      if (preBallotTitle)    preBallotTitle.textContent    = 'Pre-Election Notification';
      if (preBallotDescText) preBallotDescText.textContent = 'A single-page stamped mailing to each unit providing formal notice of the upcoming election, delivered at least 30 days before ballots are distributed';
      if (preBallotLearnMore) preBallotLearnMore.href      = portal.dataset.aboutPreBallotUrl;
    }

    // Keep isAcclamation and currentElectionValue in sync so recalc() and
    // freshness tracking use the server-confirmed values.
    isAcclamation        = !!data.is_acclamation;
    currentElectionValue = data.election_value || currentElectionValue;
    fetchTimeline();

    // Ballot row: hidden for EBA; ballot checked state mirrors server value
    const ballotsRow = document.getElementById('ballotsServiceRow');
    if (ballotsRow) ballotsRow.classList.toggle('hidden', !!data.is_acclamation);
    if (ballotsCheck) ballotsCheck.checked = data.ballots_checked;

    // In-person: hidden and forced off for EBA, restored for all other types
    const inPersonRow = document.getElementById('inPersonServiceRow');
    if (data.is_acclamation) {
      if (inPersonRow)    inPersonRow.classList.add('hidden');
      if (inPersonCheck) { inPersonCheck.checked = false; inPersonCheck.disabled = true; }
    } else {
      if (inPersonRow)    inPersonRow.classList.remove('hidden');
      if (inPersonCheck) { inPersonCheck.disabled = false; }
    }

    if (data.is_acclamation) {
      // Nomination form and reminder are fixed on for EBA — lock both
      lockService(nomCheck,      true);
      lockService(preBallotCheck, true);
    } else {
      // Restore saved ANE state if switching back from EBA
      if (aneSnapshot) {
        unlockService(nomCheck,      aneSnapshot.request_forms);
        unlockService(preBallotCheck, aneSnapshot.pre_ballots);
        patchField('request_forms', aneSnapshot.request_forms);
        patchField('pre_ballots',   aneSnapshot.pre_ballots);
        if (inPersonCheck) {
          inPersonCheck.checked = aneSnapshot.in_person;
          patchField('in_person', aneSnapshot.in_person);
        }
        aneSnapshot = null;
      } else {
        // Switching between other types (e.g. recall variants), or leaving EBA
        // on a page first loaded as EBA (no aneSnapshot) — apply server state.
        if (nomCheck) {
          nomCheck.disabled = data.nomination_form_disabled;
          nomCheck.checked  = data.nomination_form_required || (data.nomination_form_checked && !data.nomination_form_disabled);
          nomCheck.closest('label')?.classList.toggle('opacity-60',        data.nomination_form_disabled);
          nomCheck.closest('label')?.classList.toggle('cursor-not-allowed', data.nomination_form_disabled);
          nomCheck.closest('label')?.classList.toggle('cursor-pointer',    !data.nomination_form_disabled);
        }
        // Pre-ballot/notification is only ever locked for EBA. When the order was
        // first loaded as EBA, the box is still disabled here, so re-enable it and
        // restore the server's stored value — otherwise it can't be toggled on ANE.
        unlockService(preBallotCheck, data.pre_ballots_checked ?? !!preBallotCheck?.checked);
        if (inPersonCheck) {
          inPersonCheck.checked = data.in_person_checked ?? false;
        }
      }
    }

    recalc();
    checkDocumentFreshness();
  };

  const patchElectionType = (electionValue) =>
    fetch(updateElectionTypeUrl, {
      method:  'PATCH',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken(), 'Accept': 'application/json' },
      body:    JSON.stringify({ election_value: electionValue })
    }).then(r => r.ok ? r.json() : Promise.reject(r.status)).then(applyElectionTypeResponse).catch(() => {});

  const applyEbaDepthResponse = (data) => {
    const ebaRadio         = document.getElementById('mode_eba');
    const ebaLabel         = ebaRadio?.closest('label');
    const notAvailableMsg  = document.getElementById('ebaNotAvailableMsg');

    if (data.eba_qualified) {
      if (ebaRadio) {
        ebaRadio.disabled = false;
        ebaLabel?.classList.remove('opacity-50', 'cursor-not-allowed');
        ebaLabel?.classList.add('cursor-pointer');
      }
      if (notAvailableMsg) notAvailableMsg.classList.add('hidden');
    } else {
      if (ebaRadio) {
        ebaRadio.disabled = true;
        ebaLabel?.classList.add('opacity-50', 'cursor-not-allowed');
        ebaLabel?.classList.remove('cursor-pointer');
      }
      if (notAvailableMsg) notAvailableMsg.classList.remove('hidden');
      // Only revert UI and pricing when the server actually rebuilt (i.e. we were on EBA).
      // The server includes line_items only when it reverted from EBA; if the order was
      // already ANE the response has no line_items and we must not call
      // applyElectionTypeResponse — doing so would clear all service checkboxes because
      // the partial response has no nomination_form_checked / ballots_checked fields.
      if (data.election_value === 'ANE' && data.line_items) {
        const aneRadio = document.getElementById('mode_ane');
        if (aneRadio) aneRadio.checked = true;
        if (ebaRadio) ebaRadio.checked = false;
        applyElectionTypeResponse(data); // already calls fetchTimeline + checkDocumentFreshness
      } else {
        fetchTimeline();
        checkDocumentFreshness();
      }
    }
  };

  const patchEbaDepth = (qualified) =>
    fetch(updateElectionDepthUrl, {
      method:  'PATCH',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken(), 'Accept': 'application/json' },
      body:    JSON.stringify({ eba_qualified: qualified })
    }).then(r => r.ok ? r.json() : Promise.reject(r.status)).then(applyEbaDepthResponse).catch(() => {});

  // ── Service toggle listeners ──────────────────────────────────────────
  const fieldMap = {
    service_nomination_forms:                  'request_forms',
    service_pre_ballots:                       'pre_ballots',
    service_ballots:                           'ballots',
    service_electronic_consent_form:           'electronic_voting_confirmation_form',
    service_electronic_voting_rules_confirmed: 'electronic_voting_rules_confirmed',
    service_in_person:                         'in_person'
  };

  document.querySelectorAll('.service-toggle').forEach(cb => {
    cb.addEventListener('change', () => {
      if (cb.id === 'service_electronic_voting_rules_confirmed') {
        applyRulesState(cb.checked, true); // autoCheckEcf only on direct user click
      } else if (cb.id === 'service_electronic_consent_form') {
        syncEcfNote();
      } else {
        syncEcfDefault();
      }

      recalc();
      checkDocumentFreshness();

      const field = fieldMap[cb.id];
      if (field) patchField(field, cb.checked);
    });
  });

  // ── Unit adjuster listeners (debounced) ───────────────────────────────
  let unitsDebounce = null;

  const handleUnitInput = (source) => {
    const total = totalUnits;
    let paper, elec;

    if (source === 'paper') {
      paper = Math.min(Math.max(parseInt(paperInput.value, 10) || 0, 0), total);
      elec  = total - paper;
    } else {
      elec  = Math.min(Math.max(parseInt(electronicInput.value, 10) || 0, 0), total);
      paper = total - elec;
    }

    if (paperInput)      paperInput.value      = paper;
    if (electronicInput) electronicInput.value = elec;

    mailedUnits     = paper;
    electronicUnits = elec;
    recalc();
    syncElectronicDescs();
    checkDocumentFreshness();

    clearTimeout(unitsDebounce);
    unitsDebounce = setTimeout(() => patchUnits(paper), 600);
  };

  if (paperInput)      paperInput.addEventListener('input',      () => handleUnitInput('paper'));
  if (electronicInput) electronicInput.addEventListener('input', () => handleUnitInput('electronic'));

  // ── Governing-document page count listener (debounced) ────────────────
  let pagesDebounce = null;

  // Effective page count, floored at the included minimum (matches the server).
  const normalizedPages = () => {
    const v = parseInt(governingPagesInput.value, 10);
    return (isNaN(v) || v < GOVERNING_DOC_INCLUDED_PAGES) ? GOVERNING_DOC_INCLUDED_PAGES : v;
  };

  const handlePagesInput = () => {
    governingPages = normalizedPages();
    recalc();
    checkDocumentFreshness();
    clearTimeout(pagesDebounce);
    pagesDebounce = setTimeout(() => patchPages(governingPages), 600);
  };

  if (governingPagesInput) {
    governingPagesInput.addEventListener('input', handlePagesInput);
    // Normalise the displayed value once the field loses focus / commits.
    governingPagesInput.addEventListener('change', () => {
      governingPagesInput.value = normalizedPages();
      handlePagesInput();
    });
  }

  // ── Election mode radio listeners ────────────────────────────────────
  document.querySelectorAll('.election-mode-radio').forEach(radio => {
    radio.addEventListener('change', () => {
      if (!radio.checked) return;
      // Capture current ANE service state before locking into EBA
      if (radio.value === 'EBA') {
        aneSnapshot = {
          request_forms: nomCheck?.checked      ?? false,
          pre_ballots:   preBallotCheck?.checked ?? false,
          in_person:     inPersonCheck?.checked  ?? false
        };
      }
      patchElectionType(radio.value);
    });
  });

  // ── EBA qualification question listeners ─────────────────────────────
  document.querySelectorAll('.eba-qualification-radio').forEach(radio => {
    radio.addEventListener('change', () => {
      if (radio.checked) patchEbaDepth(radio.value === 'true');
    });
  });

  // ── Timeline ──────────────────────────────────────────────────────────

  const fmtDate = (iso) => {
    // Parse as local date to avoid UTC offset issues on midnight boundaries
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const renderTimeline = (data) => {
    if (!timelineEl) return;

    // ── Step rows ───────────────────────────────────────────────────────
    timelineEl.innerHTML = data.steps.map(step => {
      const compliant = step.compliant;
      const bg    = compliant ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200';
      const label = compliant ? 'text-green-800' : 'text-red-800';
      const sub   = compliant ? 'text-green-600' : 'text-red-600';
      const badge = compliant
        ? `<span class="text-xs px-2 py-0.5 bg-green-100 text-green-700 border border-green-200">On Track</span>`
        : `<span class="text-xs px-2 py-0.5 bg-red-100 text-red-700 border border-red-200">Date Passed</span>`;

      return (
        `<div class="flex items-center justify-between gap-4 p-3 border ${bg}">` +
          `<div class="min-w-0">` +
            `<p class="text-sm font-medium ${label}">${step.label}</p>` +
            `<p class="text-xs ${sub} mt-0.5 leading-relaxed">${step.description}</p>` +
          `</div>` +
          `<div class="flex flex-col items-end gap-1 flex-shrink-0">` +
            `<span class="text-sm ${label}">${fmtDate(step.date)}</span>` +
            badge +
          `</div>` +
        `</div>`
      );
    }).join('');

    // ── Accordion summary colour ────────────────────────────────────────
    if (timelineSummary) {
      const summaryBase = 'flex items-center justify-between cursor-pointer list-none px-4 py-3 transition-colors duration-200';
      if (data.compliant) {
        timelineSummary.className = summaryBase + ' bg-green-50';
        if (timelineHeading) timelineHeading.className = 'text-xs tracking-widest uppercase text-green-700';
      } else {
        timelineSummary.className = summaryBase + ' bg-red-50';
        if (timelineHeading) timelineHeading.className = 'text-xs tracking-widest uppercase text-red-700';
      }
    }

    if (timelineStatusBadge) {
      timelineStatusBadge.classList.remove('hidden');
      if (data.compliant) {
        timelineStatusBadge.className = 'text-xs px-2 py-0.5 bg-green-100 text-green-700 border border-green-200';
        timelineStatusBadge.textContent = 'On Track';
      } else {
        timelineStatusBadge.className = 'text-xs px-2 py-0.5 bg-red-100 text-red-700 border border-red-200';
        timelineStatusBadge.textContent = 'Action Needed';
      }
    }

    // ── Date input minimum ──────────────────────────────────────────────
    // min_date is always returned and reflects the earliest compliant election
    // date from today, so the browser blocks any invalid selection.
    if (meetingDateInput && data.min_date) {
      meetingDateInput.min = data.min_date;
    }
  };

  const fetchTimeline = () => {
    fetch(timelineUrl, { headers: { 'Accept': 'application/json' } })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(renderTimeline)
      .catch(() => {});
  };

  // ── Auto-save meeting date on change ─────────────────────────────────
  const fmtDateLocal = (isoString) => {
    const [yr, mo, dy] = isoString.split('-').map(Number);
    return new Date(yr, mo - 1, dy).toLocaleDateString('en-US', {
      month: 'long', day: 'numeric', year: 'numeric'
    });
  };

  const showDateTooEarlyError = (minimumDate) => {
    if (!dateChangeIndicator) return;
    dateChangeIndicator.textContent = `Choose a date on or after ${fmtDateLocal(minimumDate)}.`;
    dateChangeIndicator.classList.remove('hidden');
    // On mobile the native date picker dismisses before the user sees small
    // grey text. Apply inline styles so the message is impossible to miss.
    if (window.matchMedia('(max-width: 639px)').matches) {
      dateChangeIndicator.style.color      = '#b45309'; // amber-700
      dateChangeIndicator.style.fontSize   = '0.875rem';
      dateChangeIndicator.style.fontWeight = '500';
    }
  };

  const saveMeetingDate = (date) => {
    if (!date) return;
    if (dateChangeIndicator) {
      dateChangeIndicator.removeAttribute('style');
      dateChangeIndicator.textContent = 'Saving…';
      dateChangeIndicator.classList.remove('hidden');
    }
    fetch(updateMeetingDateUrl, {
      method:  'PATCH',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken(), 'Accept': 'application/json' },
      body:    JSON.stringify({ meeting_date: date })
    })
      .then(r => r.json().then(body => ({ ok: r.ok, body })))
      .then(({ ok, body }) => {
        if (ok) {
          if (dateChangeIndicator) {
            dateChangeIndicator.textContent = 'Saved';
            setTimeout(() => dateChangeIndicator.classList.add('hidden'), 2000);
          }
          renderTimeline(body);
          checkDocumentFreshness();
        } else if (body.minimum_meeting_date) {
          showDateTooEarlyError(body.minimum_meeting_date);
        } else {
          if (dateChangeIndicator) {
            dateChangeIndicator.textContent = 'Unable to save date.';
          }
        }
      })
      .catch(() => {
        if (dateChangeIndicator) {
          dateChangeIndicator.textContent = 'Unable to save date.';
        }
      });
  };

  if (meetingDateInput) {
    // iOS Safari fires `input` as the user scrolls the native date spinner;
    // desktop browsers fire `change` when the picker is dismissed. Both can
    // fire in quick succession on some platforms, so we debounce to ensure
    // only one PATCH goes out per user interaction.
    let meetingDateDebounce = null;
    const debouncedSaveMeetingDate = (date) => {
      clearTimeout(meetingDateDebounce);
      meetingDateDebounce = setTimeout(() => saveMeetingDate(date), 300);
    };
    meetingDateInput.addEventListener('change', () => debouncedSaveMeetingDate(meetingDateInput.value));
    meetingDateInput.addEventListener('input',  () => debouncedSaveMeetingDate(meetingDateInput.value));
  }

  // ── Initialise ────────────────────────────────────────────────────────
  if (rulesCheck && !rulesCheck.checked) applyRulesState(false, false);
  syncUnitAdjuster();
  syncEcfNote();
  syncElectronicDescs();
  recalc();
  fetchTimeline();

  // ── Email Records — shared helpers ───────────────────────────────────
  const emailAccordion = document.getElementById('emailRecordsAccordion');
  const emailBody      = document.getElementById('emailRecordsBody');

  const STATUS_CLASSES = {
    queued:           'bg-gray-100 text-gray-500',
    sent_to_provider: 'bg-blue-50 text-blue-600',
    processed:        'bg-blue-50 text-blue-600',
    delivered:        'bg-green-50 text-green-700',
    bounced:          'bg-red-50 text-red-600',
    deferred:         'bg-amber-50 text-amber-700',
    failed:           'bg-red-50 text-red-600'
  };

  function statusBadge(status) {
    const cls   = STATUS_CLASSES[status] || 'bg-gray-100 text-gray-500';
    const label = (status || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    return `<span class="inline-block px-2 py-0.5 text-[11px] font-medium tracking-wide rounded-sm ${cls}">${label}</span>`;
  }

  function renderRecord(r) {
    const eventTime = r.delivered_at || r.bounced_at || r.failed_at || r.deferred_at || r.last_event_at || '—';
    const errorRow  = r.error_detail
      ? `<div class="sm:col-span-2"><dt class="text-[11px] text-red-400 uppercase tracking-wide">Error</dt><dd class="mt-0.5 text-red-500 text-xs">${r.error_detail}</dd></div>`
      : '';
    const msgId = r.provider_message_id
      ? `<span class="font-mono text-xs break-all">${r.provider_message_id}</span>`
      : '<span class="text-gray-400">—</span>';

    return `
      <div class="px-4 py-5" data-reference-id="${r.reference_id || ''}">
        <div class="flex flex-col sm:flex-row sm:items-start gap-3 mb-4">
          <div class="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 text-sm">
            <div>
              <span class="block text-[11px] text-gray-400 mb-0.5">Email</span>
              <span class="text-gray-700">${r.email_address || '—'}</span>
            </div>
            <div>
              <span class="block text-[11px] text-gray-400 mb-0.5">About</span>
              <span class="text-gray-500">${r.about || '—'}</span>
            </div>
            <div>
              <span class="block text-[11px] text-gray-400 mb-0.5">Sent</span>
              <span class="text-gray-500">${r.created_at || '—'}</span>
            </div>
          </div>
          <div class="flex-shrink-0">${statusBadge(r.send_status)}</div>
        </div>
        <dl class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm border-t border-gray-100 pt-4">
          <div>
            <dt class="text-[11px] text-gray-400 uppercase tracking-wide">Last Event</dt>
            <dd class="mt-0.5 text-gray-600 text-xs">${eventTime}</dd>
          </div>
          <div>
            <dt class="text-[11px] text-gray-400 uppercase tracking-wide">Webhook Events</dt>
            <dd class="mt-0.5 text-gray-600 text-xs">${r.webhook_events_count}</dd>
          </div>
          <div class="sm:col-span-2">
            <dt class="text-[11px] text-gray-400 uppercase tracking-wide">SendGrid Message ID</dt>
            <dd class="mt-0.5">${msgId}</dd>
          </div>
          <div class="sm:col-span-2">
            <dt class="text-[11px] text-gray-400 uppercase tracking-wide">Reference ID</dt>
            <dd class="mt-0.5 font-mono text-xs text-gray-400 break-all">${r.reference_id || '—'}</dd>
          </div>
          ${errorRow}
        </dl>
      </div>
    `;
  }

  function renderAll(records) {
    if (!emailBody) return;
    if (!records || !records.length) {
      emailBody.innerHTML = '<p class="px-4 py-5 text-sm text-gray-400">No email records yet.</p>';
      return;
    }
    emailBody.innerHTML = records.map(renderRecord).join('');
  }

  function loadEmailRecords() {
    if (!emailAccordion || !emailBody) return;
    const url = emailAccordion.dataset.emailRecordsUrl;
    fetch(url, { headers: { Accept: 'application/json' } })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(data => renderAll(data.records))
      .catch(() => {
        emailBody.innerHTML = '<p class="px-4 py-5 text-sm text-red-400">Unable to load records.</p>';
      });
  }

  // ── Send Documents button ─────────────────────────────────────────────
  const sendBtn    = document.getElementById('sendDocumentsBtn');
  const sendLabel  = document.getElementById('sendDocumentsBtnLabel');
  const sendStatus = document.getElementById('sendDocumentsStatus');

  if (sendBtn) {
    sendBtn.addEventListener('click', () => {
      sendBtn.disabled      = true;
      sendLabel.textContent = 'Sending…';
      sendStatus.className  = 'mt-3 text-sm hidden';

      fetch(sendBtn.dataset.url, {
        method:  'POST',
        headers: { 'X-CSRF-Token': csrfToken(), 'Accept': 'application/json' }
      })
        .then(r => r.ok ? r.json() : Promise.reject(r.status))
        .then((data) => {
          sendLabel.textContent  = 'Documents Sent';
          sendStatus.textContent = 'Email queued. Delivery status will appear in Email Records below.';
          sendStatus.className   = 'mt-3 text-sm text-green-700';
          // Update the in-memory snapshot so the banner immediately clears.
          sentSnapshot     = data.snapshot || currentSnapshot();
          bannerDismissed  = false;
          checkDocumentFreshness();
          if (emailAccordion) {
            emailAccordion.open = true;
            loadEmailRecords();
          }
        })
        .catch(() => {
          sendBtn.disabled       = false;
          sendLabel.textContent  = 'Send Documents';
          sendStatus.textContent = 'Something went wrong. Please try again.';
          sendStatus.className   = 'mt-3 text-sm text-red-600';
        });
    });
  }

  // ── Email Records — accordion load + ActionCable live update ─────────
  if (emailAccordion) {
    const slug   = emailAccordion.dataset.slug;
    let   loaded = false;

    function applyUpdate(data) {
      const el = emailBody.querySelector(`[data-reference-id="${data.reference_id}"]`);
      if (!el) { loadEmailRecords(); loaded = true; return; }

      const badge = el.querySelector('span[class*="inline-block"]');
      if (badge) badge.outerHTML = statusBadge(data.send_status);

      const eventTime = data.delivered_at || data.bounced_at || data.failed_at || data.deferred_at || data.last_event_at || '—';
      const lastEventDd = el.querySelectorAll('dd')[0];
      if (lastEventDd) lastEventDd.textContent = eventTime;

      const countDd = el.querySelectorAll('dd')[1];
      if (countDd) countDd.textContent = data.webhook_events_count;
    }

    emailAccordion.addEventListener('toggle', () => {
      if (emailAccordion.open && !loaded) { loadEmailRecords(); loaded = true; }
    });

    if (window.App && window.App.cable) {
      window.App.cable.subscriptions.create(
        { channel: 'ProposalPortalChannel', slug: slug },
        {
          received(data) {
            if (data.type !== 'contact_email_record_updated') return;
            if (emailAccordion.open) applyUpdate(data);
          }
        }
      );
    }
  }
});
